import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { withQuery } from '@/lib/qs';

import { useApiClient } from '../ApiClientProvider';
import { queryKeys, type ExcludedProductFilters } from '../keys';
import type { Paginated } from '../types/common';
import type {
  ExcludedProduct,
  ExclusionBulkPayload,
  ExclusionBulkResult,
  ExclusionUpsertPayload,
} from '../types/products';

type Wrapped<T> = { data: T };

export function useExcludedProducts(filters: ExcludedProductFilters, enabled = true) {
  const api = useApiClient();
  return useQuery<Paginated<ExcludedProduct>, Error>({
    queryKey: queryKeys.excludedProducts.list(filters),
    queryFn: ({ signal }) => api.get(withQuery('/admin/excluded-products', filters), signal),
    enabled,
  });
}

/**
 * Creates or updates the exclusion for a product (keyed by `shopify_product_id`).
 * Omitted scope fields keep their current values, so the status toggle can send
 * `is_active` alone.
 */
export function useUpsertExclusion() {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation<ExcludedProduct, Error, ExclusionUpsertPayload>({
    mutationFn: async (payload) => {
      const response = await api.post<Wrapped<ExcludedProduct>>(
        '/admin/excluded-products',
        payload,
      );
      return response.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.excludedProducts.all });
    },
  });
}

/**
 * Applies one exclusion change to a whole selection in a single request.
 *
 * Firing the single-product upsert per row instead would evict the merchant's
 * rate-path settings cache and queue a storefront metafield sync once per product.
 * An empty `excluded_method_keys` / `excluded_countries` array is the stored "all
 * of them" sentinel, so it keeps covering catalog additions made later.
 */
export function useBulkUpsertExclusions() {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation<ExclusionBulkResult, Error, ExclusionBulkPayload>({
    mutationFn: async (payload) => {
      const response = await api.post<Wrapped<ExclusionBulkResult>>(
        '/admin/excluded-products/bulk',
        payload,
      );
      return response.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.excludedProducts.all });
    },
  });
}
